import config from '../config';

const AUTH_ROUTE_PREFIXES = [
  '/login',
  '/register',
  '/forgot-password',
  '/verify-account'
];

function getPrefixPath(prefixPath = config.prefixPath) {
  const rawPrefix = (prefixPath || '').toString().trim();
  if (!rawPrefix) {
    return '';
  }
  const prefixed = rawPrefix.startsWith('/') ? rawPrefix : `/${rawPrefix}`;
  return prefixed.replace(/\/+$/, '');
}

export function getDefaultPostLoginPath(prefixPath = config.prefixPath) {
  const normalizedPrefix = getPrefixPath(prefixPath);
  return normalizedPrefix ? `${normalizedPrefix}/` : '/';
}

export function getSafePostLoginPath(rawGoback, options = {}) {
  const normalizedPrefix = getPrefixPath(options.prefixPath);
  const defaultPath = getDefaultPostLoginPath(options.prefixPath);
  const origin = options.origin || (typeof window !== 'undefined' ? window.location.origin : 'http://localhost');

  if (!rawGoback) {
    return defaultPath;
  }

  const candidate = rawGoback.toString().trim();
  if (!candidate) {
    return defaultPath;
  }

  let parsedPath = '';
  try {
    const parsedUrl = new URL(candidate, origin);
    if (parsedUrl.origin !== origin) {
      return defaultPath;
    }
    parsedPath = `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
  } catch (error) {
    return defaultPath;
  }

  if (!parsedPath.startsWith('/')) {
    parsedPath = `/${parsedPath}`;
  }

  if (!normalizedPrefix) {
    return parsedPath;
  }

  if (parsedPath === normalizedPrefix || parsedPath.startsWith(`${normalizedPrefix}/`)) {
    return parsedPath;
  }

  return `${normalizedPrefix}${parsedPath}`;
}

function stripPrefix(pathname, prefixPath) {
  const normalizedPrefix = getPrefixPath(prefixPath);
  const normalizedPath = pathname && pathname.startsWith('/') ? pathname : `/${pathname || ''}`;

  if (!normalizedPrefix) {
    return normalizedPath;
  }

  if (normalizedPath === normalizedPrefix) {
    return '/';
  }

  if (normalizedPath.startsWith(`${normalizedPrefix}/`)) {
    return normalizedPath.slice(normalizedPrefix.length) || '/';
  }

  return normalizedPath;
}

export function isAuthNavigationPath(pathname, prefixPath = config.prefixPath) {
  const unprefixedPath = stripPrefix(pathname, prefixPath).replace(/\/+$/, '') || '/';
  return AUTH_ROUTE_PREFIXES.some((routePrefix) => (
    unprefixedPath === routePrefix || unprefixedPath.startsWith(`${routePrefix}/`)
  ));
}

export function buildLoginPathWithGoback(location, prefixPath = config.prefixPath) {
  const normalizedPrefix = getPrefixPath(prefixPath);
  const loginPath = `${normalizedPrefix}/login`;
  const pathname = location?.pathname || '';

  if (!pathname || isAuthNavigationPath(pathname, prefixPath)) {
    return loginPath;
  }

  const search = location?.search || '';
  const hash = location?.hash || '';
  const target = `${pathname}${search}${hash}`;
  const safeTarget = getSafePostLoginPath(target, { prefixPath });

  if (safeTarget === getDefaultPostLoginPath(prefixPath)) {
    return loginPath;
  }

  return `${loginPath}?goback=${encodeURIComponent(safeTarget)}`;
}
